// store/slices/returnSlice.ts
import { invoiceApi } from "@/services/invoiceApi";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface ReturnState {
  updating: boolean;
  error: string | null;
}

const initialState: ReturnState = {
  updating: false,
  error: null,
};

export const createReturnRequest = createAsyncThunk(
  "returns/create",
  async (data: Parameters<typeof invoiceApi.requestReturn>[0]) => {
    const response = await invoiceApi.requestReturn(data);
    return response;
  },
);

export const approveReturn = createAsyncThunk(
  "returns/approve",
  async (returnRequestId: number) => {
    await invoiceApi.approveReturn(returnRequestId);
    return returnRequestId;
  },
);

export const rejectReturn = createAsyncThunk(
  "returns/reject",
  async ({ id, rejectionReason }: { id: number; rejectionReason?: string }) => {
    await invoiceApi.rejectReturn(id, rejectionReason);
    return id;
  },
);

// ✅ New: Bulk approve returns
export const bulkApproveReturns = createAsyncThunk(
  "returns/bulkApprove",
  async (data: {
    invoiceId?: number;
    approveAll?: boolean;
    returnRequestIds?: number[];
  }) => {
    const response = await invoiceApi.bulkApproveReturns(data);
    return response;
  },
);

const returnSlice = createSlice({
  name: "returns",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createReturnRequest.pending, (state) => {
        state.updating = true;
      })
      .addCase(createReturnRequest.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(createReturnRequest.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Return request failed";
      })
      .addCase(approveReturn.pending, (state) => {
        state.updating = true;
      })
      .addCase(approveReturn.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(approveReturn.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Approval failed";
      })
      .addCase(rejectReturn.pending, (state) => {
        state.updating = true;
      })
      .addCase(rejectReturn.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(rejectReturn.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Rejection failed";
      })
      // ✅ Bulk approve
      .addCase(bulkApproveReturns.pending, (state) => {
        state.updating = true;
      })
      .addCase(bulkApproveReturns.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(bulkApproveReturns.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Bulk approval failed";
      });
  },
});

export const { clearError } = returnSlice.actions;
export default returnSlice.reducer;
